
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, Phone, Search, CheckCircle } from 'lucide-react';

const PEOShoppingProblems = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
      <Helmet>
        <title>Frustrated with PEO Shopping? We Solve the Common Problems | The PEO App</title>
        <meta name="description" content="Tired of confusing PEO quotes, endless sales calls, and complicated comparison shopping? The PEO App solves the biggest frustrations in PEO shopping with our streamlined, transparent process." />
        <link rel="canonical" href="https://thepeoapp.com/peo-shopping-problems" />
        <meta name="keywords" content="PEO shopping problems, frustrated with PEO shopping, PEO comparison nightmare, PEO sales calls, confusing PEO quotes, PEO shopping process" />
        
        <meta property="og:title" content="Frustrated with PEO Shopping? We Solve the Common Problems" />
        <meta property="og:description" content="End the PEO shopping nightmare. Get transparent quotes without endless sales calls." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/peo-shopping-problems" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frustrated with PEO Shopping?
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              You're not alone. The traditional PEO shopping process is broken. Endless sales calls, confusing quotes, 
              and no way to compare apples-to-apples. We've solved these problems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-red-600 mb-8">Common PEO Shopping Nightmares</h2>
              
              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <Phone className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Endless Sales Calls</h3>
                  <p className="text-gray-700">PEO salespeople calling you 5+ times per day, pressuring you to sign without proper comparison.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <Search className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Confusing Quotes</h3>
                  <p className="text-gray-700">Every PEO quotes differently - impossible to compare pricing, services, or value.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <Clock className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Weeks of Back-and-Forth</h3>
                  <p className="text-gray-700">The process drags on for months with endless meetings, calls, and "customized" proposals.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Hidden Costs & Surprises</h3>
                  <p className="text-gray-700">Setup fees, admin charges, and "extras" that weren't mentioned until after you've committed.</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-green-600 mb-8">How The PEO App Solves This</h2>
              
              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">No Sales Calls</h3>
                  <p className="text-gray-700">Complete your application online. PEOs respond with quotes - no phone tag required.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Standardized Quotes</h3>
                  <p className="text-gray-700">All PEOs quote using the same format - easy to compare pricing and services side-by-side.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Fast Turnaround</h3>
                  <p className="text-gray-700">Get multiple quotes within 48 hours. Make your decision on your timeline, not theirs.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900">Transparent Pricing</h3>
                  <p className="text-gray-700">All costs upfront - no hidden fees, setup charges, or surprise add-ons after you sign.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Ready to End the PEO Shopping Nightmare?</h2>
            <p className="text-lg text-gray-700 mb-8">
              Join thousands of businesses who've found their perfect PEO match without the traditional shopping headaches.
            </p>
            <Link to="/signup">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Start Your Stress-Free PEO Search
              </Button>
            </Link>
          </div>

          <div className="bg-blue-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-4">What Makes Us Different</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Search className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Vetted Providers Only</h3>
                <p className="text-sm text-gray-700">Every PEO in our network is pre-screened for quality and reliability.</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Unbiased Matching</h3>
                <p className="text-sm text-gray-700">We're not owned by any PEO - our recommendations are based on your needs only.</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Your Timeline</h3>
                <p className="text-sm text-gray-700">Move at your own pace - no pressure, no sales tactics, just honest information.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PEOShoppingProblems;
